import React, { useState } from 'react';
import { useAppDispatch } from '../store/hooks';
import { createTransaction } from '../store/slices/transactionsSlice';
import { Transaction, Account, TagMap } from '../types';
import AmountInputModal from './AmountInputModal';
import AccountSelectionModal from './AccountSelectionModal';
import { LuPlusSquare } from 'react-icons/lu';
import { buildTransactionDateTime, formatTransactionManualDescriptionDate, getTodayKolkataDateInputValue } from '../utils/dateTimeUtils';

interface AddTransactionFlowProps {
    accounts: Account[];
    tagMap: TagMap;
    preselectedAccount?: Account | null;
    buttonClassName?: string;
    buttonAriaLabel?: string;
    renderTrigger?: (openAddTransactionFlow: () => void) => React.ReactNode;
}

function AddTransactionFlow({
    accounts,
    tagMap,
    preselectedAccount,
    buttonClassName = 'text-foreground hover:text-foreground',
    buttonAriaLabel = 'Add transaction',
    renderTrigger,
}: AddTransactionFlowProps) {
    const dispatch = useAppDispatch();

    const [isAddTxSheetOpen, setIsAddTxSheetOpen] = useState<boolean>(false);
    const [transactionToEdit, setTransactionToEdit] = useState<Transaction | null>(null);
    const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState<boolean>(false);

    const handleAddTransactionClick = () => {
        const newTransaction: Transaction = {
            id: 0, // Placeholder ID, backend will assign actual ID
            amount: 0,
            description: '',
            type: 'DEBIT', // Default type, can be changed in modal
            transactionDate: buildTransactionDateTime(getTodayKolkataDateInputValue()),
            createdAt: buildTransactionDateTime(getTodayKolkataDateInputValue()), // Placeholder, backend will set this
            excludeFromAccounting: false,
            account: null, // Account will be selected later
        };
        setTransactionToEdit(newTransaction);
        setIsAddTxSheetOpen(true);
        setIsAccountSelectorOpen(false); // Ensure account selector is closed initially
    };

    const closeAddTxSheet = () => {
        setIsAddTxSheetOpen(false);
    };

    const closeAccountSelector = () => {
        setIsAccountSelectorOpen(false);
    };

    const handleTransactionSubmit = async (transactionPayload: Transaction) => {
        // Store the payload from the amount modal
        setTransactionToEdit(transactionPayload);
        // Close the amount modal and open the account selector
        setIsAddTxSheetOpen(false);
        if (preselectedAccount !== undefined) {
            await createTransactionForAccount(transactionPayload, preselectedAccount);
            return;
        }
        setIsAccountSelectorOpen(true);
    };

    const createTransactionForAccount = async (transactionPayload: Transaction, selectedAccount: Account | null) => {
        const { ...createData } = transactionPayload;

        const payloadForThunk: Partial<Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'>> = {
            ...createData,
            account: selectedAccount,
            isManualEntry: true,
            description: createData.description || `MANUAL/${createData.type}/${formatTransactionManualDescriptionDate(createData.transactionDate)}`
        };

        try {
            await dispatch(createTransaction({ transactionData: payloadForThunk })).unwrap();
            console.log("Transaction created successfully via Redux.");
        } catch (err) {
            console.error("Failed to submit transaction via Redux:", err);
        } finally {
            setIsAccountSelectorOpen(false);
            setTransactionToEdit(null);
        }
    };

    const handleAccountSelected = async (selectedAccount: Account | null) => {
        if (!transactionToEdit) {
            console.error("No transaction data available to create.");
            setIsAccountSelectorOpen(false);
            return;
        }

        await createTransactionForAccount(transactionToEdit, selectedAccount);
    };


    return (
        <>
            {renderTrigger ? (
                renderTrigger(handleAddTransactionClick)
            ) : (
                <button
                    type="button"
                    className={buttonClassName}
                    onClick={handleAddTransactionClick}
                    aria-label={buttonAriaLabel}
                >
                    <LuPlusSquare className='w-6 h-6' />
                </button>
            )}

            {accounts && (
                <AccountSelectionModal
                    isOpen={isAccountSelectorOpen}
                    onClose={closeAccountSelector}
                    onSelectAccount={handleAccountSelected}
                    accounts={accounts}
                />
            )}

            {isAddTxSheetOpen && transactionToEdit && (
                <AmountInputModal
                    // Using a key ensures the modal resets if the target account changes,
                    // though for adding new transactions, it might not be strictly necessary here.
                    // Consider if a different key strategy is needed based on behavior.
                    key={transactionToEdit.id || 'new'}
                    onClose={closeAddTxSheet}
                    transaction={transactionToEdit}
                    tagMap={tagMap}
                    onSubmitTransaction={handleTransactionSubmit}
                />
            )}
        </>
    );
}

export default AddTransactionFlow; 
